import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { moderationApi } from "@bw/shared/api";
import type {
  AppealSubmissionResponse,
  SelfModerationStatusResponse,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

export interface SubmitBanAppealVariables {
  banId: string;
  idempotencyKey: string;
  reason: string;
}

/** Token-scoped moderation status. No player identifier is accepted or cached. */
export function useMyModerationStatus(enabled = true) {
  return useQuery<SelfModerationStatusResponse>({
    queryKey: queryKeys.moderation.myStatus(),
    queryFn: () => moderationApi.getMyModerationStatus(),
    enabled,
  });
}

/**
 * Submit an appeal against the server-returned active ban. The caller owns the
 * idempotency key so an uncertain retry can reuse the exact same command.
 */
export function useSubmitBanAppeal() {
  const queryClient = useQueryClient();
  return useMutation<AppealSubmissionResponse, Error, SubmitBanAppealVariables>(
    {
      mutationFn: ({ banId, idempotencyKey, reason }) =>
        moderationApi.submitBanAppeal(banId, {
          idempotency_key: idempotencyKey,
          reason,
        }),
      onSettled: () => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.moderation.myStatus(),
        });
      },
    },
  );
}
